import { useRef, useState } from 'react'

import { EMPTY_STRING } from '../../../../../constants/common'
import { getQaSelector } from '../../../../../utils/common'
import { isAcceptedImageFile } from '../../../UploadImage.utils'
import {
  QA_NAME_UPLOAD_IMAGE,
  UPLOAD_IMAGE_ACCEPT,
} from '../../UploadImageModal.constants'

// Shared open/pick/close plumbing for per-entity upload opener hooks. With
// `initialImageUrl` (edit) open() shows the pop-up directly; without it
// (create) open() picks a file first, then opens the pop-up with it.
export function useUploadImageModal({
  aspect,
  cropShape,
  documentType,
  initialImageUrl = null,
  parent,
  qaName = QA_NAME_UPLOAD_IMAGE,
  // Edit-only: async resolver for a signed crop-stage URL (avoids the 403 the
  // raw `initialImageUrl` hits); resolved on open.
  resolveEditImageUrl = null,
  titleKey,
  withRotate,
  withUndo,
  onSave,
  onUpload,
}) {
  const [isOpen, setOpen] = useState(false)
  const [isResolvingImage, setResolvingImage] = useState(false)
  const [file, setFile] = useState(null)
  const [editImageUrl, setEditImageUrl] = useState(null)

  const fileInputRef = useRef(null)
  // Bumped on every open/close so a resolve from a superseded open is ignored.
  const openIdRef = useRef(0)
  // Aborts the in-flight source request when the modal closes or reopens.
  const resolveAbortRef = useRef(null)

  const isEdit = Boolean(initialImageUrl) || Boolean(resolveEditImageUrl)

  async function handleOpen() {
    if (!isEdit) {
      fileInputRef.current?.click()

      return
    }

    if (!resolveEditImageUrl) {
      setOpen(true)

      return
    }

    // Open in the skeleton, then (re)fetch the source. Reset any URL from a
    // previous open so a reopen starts empty instead of flashing the old image.
    resolveAbortRef.current?.abort()

    const controller = new AbortController()
    const openId = (openIdRef.current += 1)

    resolveAbortRef.current = controller

    setEditImageUrl(null)
    setResolvingImage(true)
    setOpen(true)

    const url = await resolveEditImageUrl(controller.signal).catch(
      () => initialImageUrl
    )

    if (openId === openIdRef.current) {
      setEditImageUrl(url)
      setResolvingImage(false)
    }
  }

  function handleChangeFile(event) {
    const [picked] = event.target.files

    // Defends against a non-image picked via the OS "all files" override.
    if (isAcceptedImageFile(picked)) {
      setFile(picked)
      setOpen(true)
    }

    // Reset so re-picking the same file still fires `change`.
    event.target.value = EMPTY_STRING
  }

  function handleClose() {
    // Abort the in-flight source request and invalidate its resolve so it can't
    // restore state after close.
    resolveAbortRef.current?.abort()
    openIdRef.current += 1

    setOpen(false)
    setFile(null)
    setResolvingImage(false)
  }

  const fileInputProps = {
    // `Opener` keeps it distinct from the modal's internal file input QA name.
    ...getQaSelector({ value: `${qaName}OpenerFileInput` }),
    accept: UPLOAD_IMAGE_ACCEPT,
    hidden: true,
    ref: fileInputRef,
    type: 'file',
    onChange: handleChangeFile,
  }

  const modalProps = {
    aspect,
    cropShape,
    documentType,
    initialFile: file,
    initialImageUrl: resolveEditImageUrl ? editImageUrl : initialImageUrl,
    isResolvingImage,
    parent,
    qaName,
    titleKey,
    withRotate,
    withUndo,
    onClose: handleClose,
    onSave,
    onUpload,
  }

  return { fileInputProps, handleOpen, isOpen, modalProps }
}
